import React, { useContext, useEffect, useRef, useState } from 'react'
import globalStyles from "../../Styles/global.module.scss"
import styles from "../../Styles/ComponentStyles/MainPages/Profile.module.scss"

import Grid from '@mui/material/Grid2';
import AppNavbar from '../../Components/AppNavbar/AppNavbar';
import { Avatar, CircularProgress, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import profile from '../../Assets/General/sample-profile-img.png'
import GlobalTextfield from '../../Utils/Layout/GlobalTextfield/GlobalTextfield';
import GlobalSymbols from '../../Utils/Layout/GlobalSymbols/GlobalSymbols';
import GlobalAutocomplate from '../../Utils/Layout/GlobalAutocomplate/GlobalAutoComplate';
import GlobalButton from '../../Utils/Layout/GlobalButton/GlobalButton';
import { acrylicTeam, capitalize } from '../../Utils/Constant/Constant';
import { GlobalUserContext, GlobalUserContextPayload } from '../../Context/GlobalUserDetailContext';
import { DashboardContext, DashboardDetailPayload } from '../../Context/DashboardDetailContext';
import { useSnackbar } from '../../Utils/Layout/GlobalSnackbar/GlobalSnackbar';
import agent from '../../Services/api';
import { StateOptionsInterface } from '../../models/axiosModels';
import useStateOptions from '../../Utils/Hooks/useStateOptions';
import { deleteResourceFileInterface, UserProfileDetailsPayloadModel } from '../../models/dashboardAxiosModels';
import Icons from '../../Utils/Layout/Icons/Icons';


const Profile = () => {

    const {
        setFlagLoader
    } = useContext(GlobalUserContext) as GlobalUserContextPayload

    const { userDetails, setUserDetails } = useContext(DashboardContext) as DashboardDetailPayload

    const { showSnackbar } = useSnackbar();
    const { stateOptions, getStateOptions } = useStateOptions()
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [uploadFileId, setUploadFileId] = useState<number | null>(null)
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        agencyName: "",
        contact: "",
        stateID: "",
    });

    const validateForm = () => {
        const newErrors: any = {};

        const regex = /^[a-zA-Z\s]+$/;

        // Validate first name
        if (!userDetails.firstName) {
            newErrors.firstName = "First name is required.";
        } else if (!regex.test(userDetails.firstName)) {
            newErrors.firstName = "First name can only contain letters.";
        }

        // Validate last name
        if (!userDetails.lastName) {
            newErrors.lastName = "Last name is required.";
        } else if (!regex.test(userDetails.lastName)) {
            newErrors.lastName = "Last name can only contain letters.";
        }

        // Validate agency name
        if (!userDetails.agencyName) {
            newErrors.agencyName = "Agency name is required.";
        }

        // Validate state ID
        if (!userDetails.stateID) {
            newErrors.stateID = "Please select a valid state.";
        }

        return newErrors;
    };

    const handleBlur = (field: string) => {
        const newErrors = validateForm();
        setErrors({
            ...errors,
            [field]: newErrors[field] || "",
        });
    };

    const handleProfileMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(e.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileEdit = () => {
        imageInputRef.current?.click();
        handleProfileMenuClose()
    }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        await handleProfileDelete()
        const formData = new FormData();
        formData.append("file", file);

        const response = await agent.uploadResourceFileAPI.post(formData)

        if (response?.status) {
            const responseData = response?.data
            setIsUploading(false);
            setUploadFileId(responseData.google_storage_id)
        }
        else {
            console.error("Upload failed:", response);
            setIsUploading(false);
        }

        e.target.value = ""
    }

    const handleProfileDelete = async () => {
        handleProfileMenuClose()
        if (!uploadFileId) return

        setIsUploading(true);
        if (uploadFileId) {
            const response = await agent.deleteResourceFileAPI.delete(uploadFileId);
            if (response?.status) {
                setIsUploading(false);
                setUploadFileId(0)
            }
            else {
                console.error("Upload failed:", response);
                setIsUploading(false);
            }
        }
    }

    useEffect(() => {
        getStateOptions()
        getUserDetails()
    }, [])

    useEffect(() => {
        if (uploadFileId !== null) {
            handleUpdateUserProfileDetails();
        }
    }, [uploadFileId]);

    const getUserDetails = async () => {
        try {
            setFlagLoader(true)
            const response = await agent.getUserProfileDetailsAPI.get()
            if (response?.status) {
                setUserDetails(response?.data?.user)
                setFlagLoader(false)
            } else {
                const err = response?.error?.message ? response?.error?.message : "Error fetching user details";
                showSnackbar(err, 'error')
                setFlagLoader(false)
            }
        } catch (error) {
            showSnackbar("Something Went Wrong", 'error')
        }
    }

    const handleUpdateUserProfileDetails = async () => {
        const newErrors = validateForm();
        setErrors(newErrors);

        if (Object.values(newErrors).some((error) => error !== "")) {
            return;
        }

        try {
            setFlagLoader(true)
            const payload: UserProfileDetailsPayloadModel = {
                firstName: userDetails?.firstName,
                lastName: userDetails?.lastName,
                agencyName: userDetails?.agencyName,
                contact: userDetails?.contact,
                addressLine1: userDetails?.addressLine1 ? userDetails?.addressLine1 : null,
                addressLine2: userDetails?.addressLine2 ? userDetails?.addressLine2 : null,
                city: userDetails?.city ? userDetails?.city : null,
                zipcode: userDetails?.zipcode ? userDetails?.zipcode : null,
                stateID: userDetails?.stateID ? userDetails?.stateID : null,
                profileImage: uploadFileId,
            }

            const response = await agent.userProfileDetailsUpdateAPI.put(payload)
            if (response?.status) {
                setUserDetails(response?.data?.user)
                setFlagLoader(false)
                showSnackbar("Details updated successfully", 'success')
                getUserDetails()
            } else {
                const err = response?.error?.message ? response?.error?.message : "Error fetching user details";
                showSnackbar(err, 'error')
                setFlagLoader(false)
            }
        } catch (error) {
            showSnackbar("Something Went Wrong", 'error')
        }
    }

    return (
        <>
            {/* <AppNavbar navbarHeadingName={"Profile"} /> */}
            <Grid container>
                <Grid size={{ xs: 12 }} sx={{ margin: "20px 0px 0px" }}>
                    <Grid container>
                        <Grid size={{ xs: 12 }} className={`${styles.profileWrapperItem}`}>
                            <Grid container>
                                <Grid size={{ xs: 12 }}>
                                    <Grid container className={`${styles.profileImageContainer}`}>
                                        <Grid className={`${styles.profileImageItem}`}>
                                            <Avatar
                                                src={userDetails?.profileImage ? userDetails?.profileImage : ""}
                                                sx={{
                                                    width: "100%",
                                                    height: "100px"
                                                }}
                                            />
                                            {isUploading &&
                                                <div style={{
                                                    position: "absolute", zIndex: 2, top: "30px", left: "30px"
                                                }}><CircularProgress />
                                                </div>
                                            }
                                            <div className={`${styles.profileEditImageItem}`} onClick={handleProfileMenuClick}><Icons iconName="profileEditIcon" iconHeight="28px" iconWidth="28px" /></div>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={handleProfileMenuClose}
                                            >
                                                <MenuItem onClick={handleProfileEdit}>Change Photo</MenuItem>
                                                <MenuItem onClick={handleProfileDelete}>Remove Photo</MenuItem>
                                            </Menu>
                                            <input
                                                id="avatar"
                                                accept="image/*"
                                                type="file"
                                                ref={imageInputRef}
                                                style={{ display: "none" }}
                                                onChange={handleImageUpload}
                                            />
                                        </Grid>
                                        <Grid className={`${styles.profileContentItem}`}>
                                            <Grid container>
                                                <Grid size={{ xs: 12 }} sx={{ margin: "2px 0px" }}>
                                                    <Typography
                                                        component={'span'}
                                                        className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV18} ${globalStyles.gblOnelineEllipsis}`}
                                                        sx={{ display: "block !important" }}
                                                    >
                                                        {userDetails?.username}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12 }} sx={{ margin: "2px 0px" }}>
                                                    <Typography
                                                        component={'span'}
                                                        className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15} ${globalStyles.gblOnelineEllipsis}`}
                                                        sx={{ display: "block !important" }}
                                                    >
                                                        {userDetails?.email}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid size={{ xs: 12 }} sx={{ margin: "30px 0px 25px" }}>
                                    <Grid container sx={{ columnGap: "25px", rowGap: "25px" }}>
                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Username <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter User Name'
                                                        size='medium'
                                                        fullWidth
                                                        disabled={true}
                                                        value={userDetails?.username}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Email Address <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Email Address'
                                                        size='medium'
                                                        fullWidth
                                                        disabled={true}
                                                        value={userDetails?.email}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        First Name <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter First Name'
                                                        size='medium'
                                                        fullWidth
                                                        onBlur={() => handleBlur("firstName")}
                                                        error={Boolean(errors.firstName)}
                                                        helperText={errors.firstName}
                                                        value={userDetails?.firstName}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, firstName: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Last Name <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Last Name'
                                                        size='medium'
                                                        fullWidth
                                                        onBlur={() => handleBlur("lastName")}
                                                        error={Boolean(errors.lastName)}
                                                        helperText={errors.lastName}
                                                        value={userDetails?.lastName}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, lastName: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Agency Name <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Agency Name'
                                                        size='medium'
                                                        fullWidth
                                                        onBlur={() => handleBlur("agencyName")}
                                                        error={Boolean(errors.agencyName)}
                                                        helperText={errors.agencyName}
                                                        value={userDetails?.agencyName}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, agencyName: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Phone Number <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Phone Number'
                                                        size='medium'
                                                        fullWidth
                                                        onBlur={() => handleBlur("contact")}
                                                        error={Boolean(errors.contact)}
                                                        helperText={errors.contact}
                                                        value={userDetails?.contact}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, contact: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Address Line 1
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Adresss'
                                                        size='medium'
                                                        fullWidth
                                                        value={userDetails?.addressLine1}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, addressLine1: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Address Line 2
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Adresss'
                                                        size='medium'
                                                        fullWidth
                                                        value={userDetails?.addressLine2}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, addressLine2: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Zipcode
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter Zipcode'
                                                        size='medium'
                                                        fullWidth
                                                        value={userDetails?.zipcode}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, zipcode: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        City
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalTextfield
                                                        autoComplete='off'
                                                        placeholder='Enter City'
                                                        size='medium'
                                                        fullWidth
                                                        value={userDetails?.city}
                                                        onChange={(e) => {
                                                            setUserDetails({ ...userDetails, city: e.target.value })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid className={`${styles.gblProfileTextfieldItem}`}>
                                            <Grid container>
                                                <Grid size={12} className={`${globalStyles.gblFieldSpcaing}`}>
                                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV15}`}>
                                                        Select State  <GlobalSymbols symbol={"astric"} />
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <GlobalAutocomplate
                                                        placeholder='Enter Agency Name'
                                                        size='small'
                                                        fullWidth
                                                        onBlur={() => handleBlur("stateID")}
                                                        error={Boolean(errors.stateID)}
                                                        helperText={errors.stateID}
                                                        options={stateOptions}
                                                        getOptionLabel={(options: StateOptionsInterface) => options.state_name}
                                                        value={stateOptions?.find(option => option.state_id == userDetails?.stateID) || null}
                                                        onChange={(e: any, newValue: any) => {
                                                            setUserDetails({ ...userDetails, stateID: newValue ? newValue.state_id : null })
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <GlobalButton
                                        value='Update Profile'
                                        variant='contained'
                                        size='large'
                                        className={`${styles.updateProfileBtn}`}
                                        onClick={handleUpdateUserProfileDetails}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12 }} className={`${styles.profileWrapperItem}`} sx={{ margin: "15px 0px" }}>
                            <Grid container>
                                <Grid size={{ xs: 12 }} sx={{ mb: "15px" }}>
                                    <Typography component={'span'} className={`${globalStyles.gblCWhite} ${globalStyles.globalTextV18}`}>
                                        Acrylic Team
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TableContainer sx={{ maxHeight: "40vh", overflow: "auto" }}>
                                        <Table size='small' className={`${globalStyles.gblTableStyles}`}>
                                            <TableHead className={`${globalStyles.gblTableHeadStyles}`}>
                                                <TableRow>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableHeadCell}`}
                                                        sx={{ fontSize: "14px", borderTopLeftRadius: "5px" }}
                                                    >
                                                        IMAGE
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableHeadCell}`}
                                                        sx={{ fontSize: "14px" }}
                                                    >
                                                        NAME
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableHeadCell} `}
                                                        sx={{ fontSize: "14px" }}
                                                    >
                                                        TITLE
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableHeadCell} `}
                                                        sx={{ fontSize: "14px" }}
                                                    >
                                                        EMAIL
                                                    </TableCell>
                                                    <TableCell
                                                        className={`${globalStyles.gblTableHeadCell}`}
                                                        sx={{ fontSize: "14px", borderTopRightRadius: "5px" }}
                                                    >
                                                        PHONE
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {acrylicTeam && acrylicTeam.length > 0 ? (
                                                    acrylicTeam.map((row: any, index: number) => (
                                                        <TableRow
                                                            key={index}
                                                            className={`${globalStyles.gblTableBodyRow}`}
                                                        >
                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                            >
                                                                {row.image}
                                                            </TableCell>

                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                            >
                                                                {row.name}
                                                            </TableCell>

                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                            >
                                                                {row.title}
                                                            </TableCell>

                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                            >
                                                                {row.email}
                                                            </TableCell>

                                                            <TableCell
                                                                className={`${globalStyles.gblTableCellText}`}
                                                            >
                                                                {row.phone}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={5}
                                                            className={`${globalStyles.gblTableCellText}`}
                                                            align="center"
                                                            sx={{ backgroundColor: "#394457", color: "#ffffff" }}
                                                        >
                                                            No data found
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default Profile